import { type ReactElement, useState, useEffect } from 'react';
import { NeonButtonFooter, NeonModalDialog, NeonSelectField } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import { BULK_SUBJECT_AREA_MODAL_TEXT } from '@/constants/course-catalog-constants';
import type { SubjectArea } from '@/types';
import { formatSubjectAreaOptionLabel, subjectAreaToOptionValue } from '@/utils/subject-area-utils';
import styles from './bulk-change-subject-area-modal.module.scss';

export interface BulkChangeSubjectAreaModalProps {
  isOpen: boolean;
  selectedCount: number;
  subjectAreas: SubjectArea[];
  /** Composite option value from `subjectAreaToOptionValue`; parent resolves to API subject area name. */
  onSave: (subjectAreaOptionValue: string) => void;
  onClose: () => void;
  isSaving: boolean;
}

export function BulkChangeSubjectAreaModal({
  isOpen,
  selectedCount,
  subjectAreas,
  onSave,
  onClose,
  isSaving,
}: BulkChangeSubjectAreaModalProps): ReactElement | null {
  const [selectedSubjectArea, setSelectedSubjectArea] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedSubjectArea(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const bodyText = BULK_SUBJECT_AREA_MODAL_TEXT.Body.replace('[#]', String(selectedCount));

  const options = subjectAreas.map((sa) => ({
    text: formatSubjectAreaOptionLabel(sa),
    value: subjectAreaToOptionValue(sa),
  }));

  const handleSave = (): void => {
    if (selectedSubjectArea === null) {
      return;
    }
    onSave(selectedSubjectArea);
  };

  return (
    <NeonModalDialog
      id="bulk-change-subject-area-modal"
      neonDialogHasClosed={onClose}
      dataSize="small"
    >
      <div data-slot="dialog-header-title">{BULK_SUBJECT_AREA_MODAL_TEXT.Title}</div>
      <div data-slot="dialog-body">
        <p className={`${styles.bodyIntro} __neon__text`}>{bodyText}</p>
        <div className={styles.bulkSubjectAreaField}>
          <NeonSelectField
            id="bulk-subject-area-select"
            dataLabelText="Select subject area"
            dataShowLabel="false"
            dataPlaceholderText={BULK_SUBJECT_AREA_MODAL_TEXT.Placeholder}
            options={options}
            modelValue={selectedSubjectArea ?? undefined}
            modelValueChange={(value: string | undefined) =>
              setSelectedSubjectArea(value !== undefined ? value : null)
            }
          />
        </div>
      </div>
      <div data-slot="dialog-button-footer">
        <NeonButtonFooter
          id="bulk-change-subject-area-footer"
          buttons={[
            {
              id: 'bulk-change-subject-area-cancel',
              text: BULK_SUBJECT_AREA_MODAL_TEXT.Cancel,
              type: NeonButtonTypes.Borderless,
              onClick: (e): void => {
                (e.target as HTMLElement)?.dispatchEvent(
                  new Event('neonDialogClose', { bubbles: true })
                );
              },
            },
          ]}
          primaryButton={{
            id: 'bulk-change-subject-area-save',
            text: BULK_SUBJECT_AREA_MODAL_TEXT.Confirm,
            type: NeonButtonTypes.Primary,
            disabled: isSaving || selectedSubjectArea === null,
            onClick: handleSave,
          }}
        />
      </div>
    </NeonModalDialog>
  );
}
